import type { LiveParsedSpot, LiveSpotTemplate, NewBreaks } from "@hobbyshopify/backend-client";
import { calculateBreak, dollarsToCents } from "./live";

/** What the host has typed into the builder: one box, so the money is that box's. */
export interface BoxDraft {
  title: string;
  /** What is being opened, from inventory; null for a box typed in by hand. */
  sealedProductId: string | null;
  /** The spot list, by template key; null until the host picks one. */
  templateKey: string | null;
  cost: string;
  marketValue: string;
  multiplier: number;
  spotText: string;
  /** "Not earlier than", as a `datetime-local` value; empty means any time. */
  breakTime: string;
  coverImageUrl: string | null;
}

export const EMPTY_BOX_DRAFT: BoxDraft = {
  title: "",
  sealedProductId: null,
  templateKey: null,
  cost: "",
  marketValue: "",
  multiplier: 200,
  spotText: "",
  breakTime: "",
  coverImageUrl: null,
};

export interface BoxProjection {
  pricePerSpot: number;
  gross: number;
  profit: number;
}

export interface BoxPlan {
  costCents: number;
  /** What it is worth defaults to what it cost. */
  marketValueCents: number;
  usingPaste: boolean;
  labels: string[];
  perSlotPricesCents: (number | null)[] | undefined;
  sellerNotes: (string | null)[] | undefined;
  category: NewBreaks["category"];
  /** Only worth showing per-spot money the host did not type themselves. */
  evenlyPriced: boolean;
  spotRows: { slot: number; label: string; priceCents: number | null }[];
  projection: BoxProjection | null;
  /** A name, an image, and spots with money to price them from: the condition for putting it on sale. */
  ready: boolean;
}

/** Priced the way the server prices the break. Pasted spots win over the template. */
export function planBox(
  draft: BoxDraft,
  templates: readonly LiveSpotTemplate[],
  pasted: readonly LiveParsedSpot[],
): BoxPlan {
  const costCents = dollarsToCents(draft.cost);
  const typedMarketCents = dollarsToCents(draft.marketValue);
  const marketValueCents = typedMarketCents > 0 ? typedMarketCents : costCents;

  const template = templates.find((t) => t.key === draft.templateKey) ?? null;
  const usingPaste = pasted.length > 0;
  const labels = usingPaste ? pasted.map((s) => s.label) : (template?.labels ?? []);
  const perSlotPricesCents = usingPaste ? pasted.map((s) => s.priceCents) : undefined;
  const sellerNotes = usingPaste ? pasted.map((s) => s.note) : undefined;
  const category: NewBreaks["category"] = usingPaste
    ? "CUSTOM"
    : ((template?.category as NewBreaks["category"] | undefined) ?? "CUSTOM");
  const evenlyPriced = !usingPaste || pasted.every((spot) => spot.priceCents === null);
  const spotRows = labels.map((label, index) => ({
    slot: index + 1,
    label,
    priceCents: perSlotPricesCents?.[index] ?? null,
  }));
  const projection = projectStream({
    labels,
    perSlotPricesCents,
    marketValueCents,
    costCents,
    multiplier: draft.multiplier,
  });

  return {
    costCents,
    marketValueCents,
    usingPaste,
    labels,
    perSlotPricesCents,
    sellerNotes,
    category,
    evenlyPriced,
    spotRows,
    projection,
    ready: draft.title.trim() !== "" && draft.coverImageUrl !== null && projection !== null,
  };
}

function projectStream(o: {
  labels: string[];
  perSlotPricesCents: (number | null)[] | undefined;
  marketValueCents: number;
  costCents: number;
  multiplier: number;
}): BoxProjection | null {
  const { labels, perSlotPricesCents, marketValueCents, costCents, multiplier } = o;
  if (labels.length === 0 || marketValueCents <= 0) return null;

  const priced = calculateBreak({
    marketValueCents,
    costCents,
    targetMultiplierPct: multiplier,
    slotCount: labels.length,
  });
  // Anything the host did not price takes the recommendation.
  const gross = labels.reduce(
    (sum, _label, slot) => sum + (perSlotPricesCents?.[slot] ?? priced.recommendedPriceCents),
    0,
  );
  return { pricePerSpot: priced.recommendedPriceCents, gross, profit: gross - costCents };
}

export function boxRequest(draft: BoxDraft, plan: BoxPlan): NewBreaks {
  return {
    title: draft.title.trim(),
    category: plan.category,
    costCents: plan.costCents,
    marketValueCents: plan.marketValueCents,
    targetMultiplierPct: draft.multiplier,
    labels: plan.labels,
    perSlotPricesCents: plan.perSlotPricesCents,
    sellerNotes: plan.sellerNotes,
    breakNotBefore: draft.breakTime ? new Date(draft.breakTime) : undefined,
    sealedProductId: draft.sealedProductId ?? undefined,
    imageUrl: draft.coverImageUrl ?? undefined,
  };
}
